import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, router } from '@inertiajs/react'
import { getDateLocale } from '@/lib/i18n'
import { AdminLayout } from '@/layouts/admin-layout'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ShieldAlertIcon, ExternalLinkIcon, XIcon } from 'lucide-react'

interface Organization {
  id: number
  name: string
  deactivated: boolean
}

interface Project {
  id: number
  name: string
}

interface ReviewedBy {
  id: number
  name: string
}

interface SecurityViolation {
  id: number
  event_type: string
  reason: string | null
  command_preview: string | null
  status: 'pending' | 'false_positive' | 'real_attack'
  review_notes: string | null
  reviewed_at: string | null
  reviewed_by: ReviewedBy | null
  organization: Organization | null
  project: Project | null
  created_at: string
}

interface Pagination {
  page: number
  pages: number
  count: number
  from: number
  to: number
}

interface Filters {
  organizations: Array<{ id: number; name: string }>
  event_types: string[]
  selected_organization_id?: string
  selected_event_type?: string
  selected_status?: string
  start_date?: string
  end_date?: string
}

interface SecurityViolationsIndexProps {
  violations: SecurityViolation[]
  pagination: Pagination
  filters: Filters
}

function StatusBadge({ status }: { status: SecurityViolation['status'] }) {
  const { t } = useTranslation('admin')

  const variants: Record<SecurityViolation['status'], { variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string }> = {
    pending: { variant: 'outline', className: 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700' },
    false_positive: { variant: 'secondary', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
    real_attack: { variant: 'destructive', className: '' }
  }

  const config = variants[status]

  return (
    <Badge variant={config.variant} className={config.className}>
      {t(`securityViolations.status.${status}`)}
    </Badge>
  )
}

function Index({ violations, pagination, filters }: SecurityViolationsIndexProps) {
  const { t } = useTranslation('admin')
  const [organizationId, setOrganizationId] = useState(filters.selected_organization_id || 'all')
  const [eventType, setEventType] = useState(filters.selected_event_type || 'all')
  const [statusFilter, setStatusFilter] = useState(filters.selected_status || 'all')
  const [startDate, setStartDate] = useState(filters.start_date || '')
  const [endDate, setEndDate] = useState(filters.end_date || '')
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false)
  const [reviewStatus, setReviewStatus] = useState<string>('false_positive')
  const [reviewNotes, setReviewNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSearch = () => {
    const params: Record<string, string> = {}
    if (organizationId && organizationId !== 'all') params.organization_id = organizationId
    if (eventType && eventType !== 'all') params.event_type = eventType
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate

    router.get('/admin/security_violations', params, { preserveState: true })
  }

  const handleReset = () => {
    setOrganizationId('all')
    setEventType('all')
    setStatusFilter('all')
    setStartDate('')
    setEndDate('')
    router.get('/admin/security_violations', {}, { preserveState: true })
  }

  const toggleSelectAll = () => {
    if (selectedIds.length === violations.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(violations.map(v => v.id))
    }
  }

  const toggleSelect = (id: number) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  const handleBulkReview = () => {
    if (selectedIds.length === 0) return
    setReviewDialogOpen(true)
  }

  const submitBulkReview = () => {
    setIsSubmitting(true)
    router.post('/admin/security_violations/bulk_review', {
      violation_ids: selectedIds,
      status: reviewStatus,
      notes: reviewNotes || undefined
    }, {
      onFinish: () => {
        setIsSubmitting(false)
        setReviewDialogOpen(false)
        setSelectedIds([])
        setReviewNotes('')
      }
    })
  }

  const clearSelection = () => {
    setSelectedIds([])
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-2 py-2">
          <div className="px-4 lg:px-6">
            {/* Page Header */}
            <div className="mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <ShieldAlertIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">
                    {t('securityViolations.pageTitle')}
                  </h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('securityViolations.pageDescription', { count: pagination.count })}
                  </p>
                </div>
              </div>
            </div>

            {/* Filters */}
            <Card className="shadow-none mb-6 p-3">
              <div className="flex items-end justify-between flex-wrap gap-3">
                <div className="flex items-end flex-wrap gap-3 flex-1">
                  <div className="flex-1 min-w-[180px]">
                    <label className="text-xs font-medium text-foreground block mb-1">
                      {t('securityViolations.filters.organization')}
                    </label>
                    <Select value={organizationId} onValueChange={setOrganizationId}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder={t('securityViolations.filters.allOrganizations')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('securityViolations.filters.allOrganizations')}</SelectItem>
                        {filters.organizations.map((org) => (
                          <SelectItem key={org.id} value={org.id.toString()}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-[160px]">
                    <label className="text-xs font-medium text-foreground block mb-1">
                      {t('securityViolations.filters.eventType')}
                    </label>
                    <Select value={eventType} onValueChange={setEventType}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder={t('securityViolations.filters.allTypes')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('securityViolations.filters.allTypes')}</SelectItem>
                        {filters.event_types.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-[160px]">
                    <label className="text-xs font-medium text-foreground block mb-1">
                      {t('securityViolations.filters.status')}
                    </label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder={t('securityViolations.filters.allStatuses')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('securityViolations.filters.allStatuses')}</SelectItem>
                        <SelectItem value="pending">{t('securityViolations.status.pending')}</SelectItem>
                        <SelectItem value="false_positive">{t('securityViolations.status.false_positive')}</SelectItem>
                        <SelectItem value="real_attack">{t('securityViolations.status.real_attack')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 flex gap-2 min-w-[280px]">
                    <div className="flex-1">
                      <label className="text-xs font-medium text-foreground block mb-1">
                        {t('securityViolations.filters.startDate')}
                      </label>
                      <DatePicker
                        value={startDate}
                        onChange={setStartDate}
                        placeholder={t('securityViolations.filters.startDate')}
                        className="h-9 text-sm"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs font-medium text-foreground block mb-1">
                        {t('securityViolations.filters.endDate')}
                      </label>
                      <DatePicker
                        value={endDate}
                        onChange={setEndDate}
                        placeholder={t('securityViolations.filters.endDate')}
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <Button onClick={handleSearch} className="h-9 px-4 text-sm">
                    {t('securityViolations.buttons.search')}
                  </Button>
                  <Button onClick={handleReset} variant="outline" className="h-9 px-4 text-sm">
                    {t('securityViolations.buttons.reset')}
                  </Button>
                </div>
              </div>
            </Card>

            {/* Violations Table */}
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
                <table className="w-full">
                  <thead className="bg-muted sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <Checkbox
                          checked={violations.length > 0 && selectedIds.length === violations.length}
                          onCheckedChange={toggleSelectAll}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('securityViolations.table.timestamp')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('securityViolations.table.organization')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('securityViolations.table.project')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('securityViolations.table.eventType')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('securityViolations.table.status')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('securityViolations.table.reason')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('securityViolations.table.reviewedBy')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {violations.length > 0 ? (
                      violations.map((violation) => (
                        <tr key={violation.id} className="hover:bg-muted transition-colors">
                          <td className="px-4 py-4">
                            <Checkbox
                              checked={selectedIds.includes(violation.id)}
                              onCheckedChange={() => toggleSelect(violation.id)}
                            />
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-foreground">
                              {new Date(violation.created_at).toLocaleString(getDateLocale(), {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {violation.organization ? (
                              <div className="flex items-center gap-2">
                                <Link
                                  href={`/admin/organizations/${violation.organization.id}`}
                                  className="text-sm font-medium text-foreground hover:text-primary flex items-center gap-1"
                                >
                                  {violation.organization.name}
                                  <ExternalLinkIcon className="h-3 w-3" />
                                </Link>
                                {violation.organization.deactivated && (
                                  <Badge variant="destructive" className="text-xs">
                                    {t('securityViolations.deactivated')}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {violation.project ? (
                              <Link
                                href={`/admin/projects/${violation.project.id}`}
                                className="text-sm text-foreground hover:text-primary flex items-center gap-1"
                              >
                                {violation.project.name}
                                <ExternalLinkIcon className="h-3 w-3" />
                              </Link>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <Badge variant="destructive">
                              {violation.event_type}
                            </Badge>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <StatusBadge status={violation.status} />
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm text-foreground max-w-xs truncate">
                              {violation.reason || '-'}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {violation.reviewed_by ? (
                              <div className="text-sm text-foreground">
                                {violation.reviewed_by.name}
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center text-muted-foreground">
                          {t('securityViolations.empty')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="px-6 py-4 flex items-center justify-between border-t border-border bg-card">
                  <div className="text-sm text-foreground">
                    {t('securityViolations.pagination.showing', {
                      from: pagination.from,
                      to: pagination.to,
                      count: pagination.count
                    })}
                  </div>
                  <div className="flex gap-2">
                    {pagination.page > 1 && (
                      <Button
                        variant="outline"
                        onClick={() => router.get(`/admin/security_violations?page=${pagination.page - 1}`, {}, { preserveState: true })}
                      >
                        {t('securityViolations.pagination.previous')}
                      </Button>
                    )}
                    {pagination.page < pagination.pages && (
                      <Button
                        variant="outline"
                        onClick={() => router.get(`/admin/security_violations?page=${pagination.page + 1}`, {}, { preserveState: true })}
                      >
                        {t('securityViolations.pagination.next')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Bulk Actions Bar */}
            {selectedIds.length > 0 && (
              <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-card border border-border rounded-lg shadow-lg px-6 py-4 flex items-center gap-4 z-50">
                <span className="text-sm font-medium text-foreground">
                  {t('securityViolations.bulkActions.selected', { count: selectedIds.length })}
                </span>
                <Button onClick={handleBulkReview} size="sm">
                  {t('securityViolations.bulkActions.review')}
                </Button>
                <Button onClick={clearSelection} variant="ghost" size="sm">
                  <XIcon className="h-4 w-4 mr-1" />
                  {t('securityViolations.bulkActions.clear')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedIds.length === 1
                ? t('securityViolations.review.title')
                : t('securityViolations.review.bulkTitle', { count: selectedIds.length })}
            </DialogTitle>
            <DialogDescription>
              {t('securityViolations.review.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                {t('securityViolations.filters.status')}
              </label>
              <Select value={reviewStatus} onValueChange={setReviewStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="false_positive">{t('securityViolations.status.false_positive')}</SelectItem>
                  <SelectItem value="real_attack">{t('securityViolations.status.real_attack')}</SelectItem>
                  <SelectItem value="pending">{t('securityViolations.status.pending')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                {t('securityViolations.review.notes')}
              </label>
              <Textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder={t('securityViolations.review.notesPlaceholder')}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              {t('securityViolations.buttons.cancel')}
            </Button>
            <Button onClick={submitBulkReview} disabled={isSubmitting}>
              {isSubmitting ? t('securityViolations.review.submitting') : t('securityViolations.review.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
